import React, { useEffect, useState } from 'react';
import { Card, Spin } from 'antd';
import { AnchorPlanFile } from '../../../interfaces/OrderDetail';
import { getUserFromStorage } from '../../../util/localStorage.util';

interface AnchorPlanViewerProps {
  anchorPlan: AnchorPlanFile | null;
}

function useResolvedUrl(anchorPlan: AnchorPlanFile | null): { url: string | null; loading: boolean; error: string | null } {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!anchorPlan) {
      setUrl(null);
      setError(null);
      return;
    }

    if (anchorPlan.blobFile) {
      setUrl(anchorPlan.link);
      setError(null);
      return;
    }

    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);

    const storageInfo = getUserFromStorage();
    const token = storageInfo ? JSON.parse(storageInfo).data.token : '';

    fetch(anchorPlan.link, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (!res.ok) {
          setError('Anchor plan is unavailable.');
          return null;
        }
        return res.blob();
      })
      .then((blob) => {
        if (!blob) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => setError('Failed to load anchor plan.'))
      .finally(() => setLoading(false));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [anchorPlan?.link, anchorPlan?.blobFile]);

  return { url, loading, error };
}

export const AnchorPlanViewer: React.FC<AnchorPlanViewerProps> = ({ anchorPlan }) => {
  const { url, loading, error } = useResolvedUrl(anchorPlan);

  if (!anchorPlan) {
    return (
      <Card style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span>No anchor plan on file</span>
      </Card>
    );
  }

  return (
    <Card bodyStyle={{ padding: 0 }} style={{ height: 400, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {loading ? (
        <Spin />
      ) : error ? (
        <span>{error}</span>
      ) : url ? (
        <iframe
          src={url}
          title="Anchor Plan"
          style={{ width: '100%', height: 400, border: 'none' }}
        />
      ) : (
        <Spin />
      )}
    </Card>
  );
};
